import { oc } from "@orpc/contract";
import { z } from "zod";
import { IntegrationBaseSchema, IntegrationKey } from "./base";

/**
 * Oracle EBS Integration Settings Schema
 *
 * Holds the per-org connection credentials for the Oracle EBS REST endpoints
 * (Order Import + Order Status), which previously lived only in `EBS_API_*`
 * environment variables. Business config (query server, org name, defaults,
 * poll cadence) stays in `ModuleConfig.oracleEbs`.
 *
 * `password` is optional on updates — when omitted the backend preserves the
 * existing encrypted value. It is always required on first save.
 */
export const OracleEbsIntegrationSettingsSchema = z.object({
  baseUrl: z.string(),
  username: z.string(),
  password: z.string().optional(),
});

/**
 * Oracle EBS Integration Settings Type
 */
export type OracleEbsIntegrationSettings = z.infer<
  typeof OracleEbsIntegrationSettingsSchema
>;

/**
 * Oracle EBS integration variant (without base fields)
 */
export const OracleEbsIntegrationSettingsSchemaWithoutBase = z.object({
  key: z.literal(IntegrationKey.OracleEbs),
  settings: OracleEbsIntegrationSettingsSchema,
});

/**
 * Full Oracle EBS integration schema (with base fields)
 */
export const OracleEbsIntegrationSchema = IntegrationBaseSchema.merge(
  OracleEbsIntegrationSettingsSchemaWithoutBase,
);

/**
 * Oracle EBS Integration Type
 */
export type OracleEbsIntegration = z.infer<typeof OracleEbsIntegrationSchema>;

// ── Contracts ──

const testConnection = oc
  .input(z.object({ integrationId: z.string().uuid() }))
  .output(
    z.object({
      success: z.boolean(),
      message: z.string(),
    }),
  );

export const oracleEbsContracts = {
  testConnection,
} as const;

export type OracleEbsContracts = typeof oracleEbsContracts;
